import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { LazyImportProductVersionBundleModal } from '@client/components/products-v2/LazyImportProductVersionBundleModal';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Dropdown } from 'antd';
import { ChevronDown, FileText, FileUp, Link2, Package } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/products-v2')({
    staticData: {
        metadata: {
            title: 'Products',
        },
        ui: {
            icon: Package,
            title: 'Products',
            subtitle: 'Manage products and their versions',
            useTitleForChildren: true,
            menuDescription: 'Products, their versions and lodgement links',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:update'],
        featureFlag: 'is-products-v2-enabled',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductsV2Search />
        </PageController>
    );
}

type QueryParams = RouterInputs['product']['listProducts'];

const columnHelper = createColumnHelper<
    RouterOutputs['product']['listProducts']['rows'][number],
    QueryParams['orderBy']
>();

function ProductsV2Search() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    // Lodgement v2 (CUR-1407): the public links console lives on the product; server-gated too.
    // Moving a version between environments as a file (CUR-1893). An import can publish a
    // claim form, so on top of this page's own permission it needs what the form designer needs.
    const { checkAll } = usePermissions();
    const canImportVersion = checkAll(['form_schema:create', 'form_schema:update']);
    const [isImportOpen, setIsImportOpen] = useState(false);
    // Mounted from the first click on, so closing can still animate and report what was imported.
    const [hasOpenedImport, setHasOpenedImport] = useState(false);

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending, error, isFetching, refetch } = trpc.product.listProducts.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No products yet' },
        columnVisibility: {
            enabled: true,
            persistKey: 'products-v2-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                sortable: 'type',
            }),
            columnHelper.display({
                id: 'insurer',
                header: 'Insurer',
                cell({ row }) {
                    return row.Insurer?.name ?? '—';
                },
            }),
            columnHelper.actions({
                width: 200,
                cell({ row }) {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Button.Edit.Link
                                to="/console/$orgId/configuration/claims-products/products-v2/edit/$productId"
                                params={{
                                    orgId,
                                    productId: row.id,
                                }}
                            />
                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: [
                                        {
                                            key: 'versions',
                                            label: 'Versions',
                                            icon: <FileText size={14} className="text-blue-600" />,
                                            disabled: !row.lastProductVersionId,
                                            onClick() {
                                                if (!row.lastProductVersionId) {
                                                    return;
                                                }

                                                navigate({
                                                    to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                                                    params: {
                                                        orgId,
                                                        productId: row.id,
                                                        productVersionId: row.lastProductVersionId,
                                                    },
                                                });
                                            },
                                        },
                                        {
                                            key: 'lodgement-links',
                                            label: 'Lodgement links',
                                            icon: <Link2 size={14} className="text-blue-600" />,
                                            onClick() {
                                                navigate({
                                                    to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/lodgement-links',
                                                    params: {
                                                        orgId,
                                                        productId: row.id,
                                                    },
                                                });
                                            },
                                        },
                                    ],
                                }}
                            >
                                <Button type="tertiary" icon={<ChevronDown />} iconPosition="end">
                                    Configure
                                </Button>
                            </Dropdown>
                        </div>
                    );
                },
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />

            <TableToolbar
                actions={
                    <Space>
                        {canImportVersion && (
                            <Button
                                type="secondary"
                                icon={<FileUp />}
                                onClick={() => {
                                    setHasOpenedImport(true);
                                    setIsImportOpen(true);
                                }}
                            >
                                Import product version
                            </Button>
                        )}
                        <Button.Add.Link
                            to="/console/$orgId/configuration/claims-products/products-v2/add"
                            params={{
                                orgId,
                            }}
                            variant="primary"
                            label="New product"
                        />
                    </Space>
                }
            />
            {canImportVersion && hasOpenedImport && (
                <LazyImportProductVersionBundleModal
                    open={isImportOpen}
                    onClose={(imported) => {
                        setIsImportOpen(false);
                        if (!imported) {
                            return;
                        }
                        navigate({
                            to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                            params: {
                                orgId,
                                productId: imported.productId,
                                productVersionId: imported.productVersionId,
                            },
                        });
                    }}
                />
            )}
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/products-v2/edit/$productId',
                                params: {
                                    orgId,
                                    productId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
